import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import Layout from '../components/Layout';

type Character = { id: number; name: string };
type CharactersResponse = { success: boolean; data: Character[] };
type CreateCharacterResponse = { success: boolean; data: Character; message?: string };

function CharacterCreator() {
    const { apiRequest } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [characters, setCharacters] = useState<Character[]>([]);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        apiRequest<CharactersResponse>('/api/characters/')
            .then((result) => setCharacters(result.data))
            .catch(() => setError('Could not load your characters.'));
    }, [apiRequest]);

    async function createCharacter(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError('Enter a character name.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const result = await apiRequest<CreateCharacterResponse>('/api/characters/', 'POST', { name: trimmedName });
            navigate(`/characters/${result.data.id}`);
        } catch {
            setError('Could not create the character.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Layout>
            <div className="container my-4">
                <h1>Your characters</h1>

                <form className="row g-2 mt-2" onSubmit={createCharacter}>
                    <div className="col-sm-6">
                        <label className="visually-hidden" htmlFor="character-name">Character name</label>
                        <input
                            id="character-name"
                            className="form-control"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Character name"
                            maxLength={255}
                        />
                    </div>
                    <div className="col-auto">
                        <button className="btn btn-primary" type="submit" disabled={saving}>
                            {saving ? 'Creating…' : 'Create character'}
                        </button>
                    </div>
                </form>

                {error && <div className="alert alert-danger mt-3" role="alert">{error}</div>}

                {characters.length > 0 && (
                    <div className="list-group mt-4">
                        {characters.map((character) => (
                            <button
                                className="list-group-item list-group-item-action"
                                key={character.id}
                                onClick={() => navigate(`/characters/${character.id}`)}
                            >
                                {character.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default CharacterCreator;
